import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/Card'
import { StatCard } from '@components/ui/StatCard'
import { ResponsiveAlignedScrollableLayout } from '@components/layout/ResponsiveAlignedScrollableLayout'
import { useStats } from '@hooks/useStats'
import { useAuthStore } from '@stores/authStore'
import { useNavigate } from 'react-router-dom'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { IngredientIcon, ProductIcon, BrandIcon, UsersIcon } from '@components/icons'

const WelcomeHeader = () => {
  const { user } = useAuthStore()
  const { refetch } = useStats()

  return (
    <button
      onClick={refetch}
      className="text-left hover:opacity-80 transition-opacity"
    >
      <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
        Bienvenue {user?.firstName} 👋
      </h1>
    </button>
  )
}

const StatsSection = () => {
  const { stats, isLoading, error } = useStats()

  // En cas d'erreur, ne rien afficher
  if (error) {
    console.error('❌ Stats error:', error)
    return null
  }

  // Pendant le chargement, afficher des skeletons
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-gray-200 dark:bg-gray-700 rounded-lg h-24"></div>
          </div>
        ))}
      </div>
    )
  }

  // Si pas de stats (et pas d'erreur), ne rien afficher
  if (!stats) {
    return null
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <StatCard
        label="Nombre total d'ingrédients"
        value={stats.totalIngredients.toString()}
        icon={<IngredientIcon className="h-6 w-6" />}
      />
      <StatCard
        label="Nombre total de produits"
        value={stats.totalProducts.toString()}
        icon={<ProductIcon className="h-6 w-6" />}
      />
      <StatCard
        label="Nombre total de marques"
        value={stats.totalBrands.toString()}
        icon={<BrandIcon className="h-6 w-6" />}
      />
      <StatCard
        label="Nombre total d'utilisateurs"
        value={stats.totalUsers.toString()}
        icon={<UsersIcon className="h-6 w-6" />}
      />
    </div>
  )
}

interface UncategorizedTileProps {
  title: string
  icon: React.ReactNode
  count: number
  onTap?: () => void
}

const UncategorizedTile = ({ title, icon, count, onTap }: UncategorizedTileProps) => {
  return (
    <div
      className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
      onClick={onTap}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && onTap) {
          e.preventDefault()
          onTap()
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${title}: ${count} éléments`}
    >
      <div className="flex items-center space-x-3">
        <div className="h-6 w-6 text-red-600 dark:text-red-400">
          {icon}
        </div>
        <span className="text-red-600 dark:text-red-400 font-normal">{title}</span>
      </div>
      <div className="flex items-center space-x-2">
        <span className="text-gray-900 dark:text-white font-normal">{count}</span>
        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
      </div>
    </div>
  )
}

const UncategorizedSection = () => {
  const { stats, isLoading, error } = useStats()
  const navigate = useNavigate()

  // En cas d'erreur ou de chargement, ne rien afficher
  if (error || isLoading || !stats) {
    return null
  }

  const hasUncategorizedItems = 
    stats.totalUncategorizedIngredients > 0 ||
    stats.totalUncategorizedProducts > 0 ||
    stats.totalUncategorizedBrands > 0

  if (!hasUncategorizedItems) {
    return null
  }

  return (
    <div className="max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Éléments à catégoriser</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {stats.totalUncategorizedIngredients > 0 && (
            <UncategorizedTile
              title="Ingrédients à catégoriser"
              icon={<IngredientIcon className="h-6 w-6" />}
              count={stats.totalUncategorizedIngredients}
              onTap={() => {
                // Navigate to ingredients page with "À valider" filter
                navigate('/ingredients', { state: { toValidate: true } })
              }}
            />
          )}
          {stats.totalUncategorizedProducts > 0 && (
            <UncategorizedTile
              title="Produits à catégoriser"
              icon={<ProductIcon className="h-6 w-6" />}
              count={stats.totalUncategorizedProducts}
              onTap={() => {
                // Navigate to products page with "À catégoriser" filter
                navigate('/products', { state: { toValidate: true } })
              }}
            />
          )}
          {stats.totalUncategorizedBrands > 0 && (
            <UncategorizedTile
              title="Marques à catégoriser"
              icon={<BrandIcon className="h-6 w-6" />}
              count={stats.totalUncategorizedBrands}
              onTap={() => {
                // Navigate to brands page with filter
                navigate('/brands', { state: { toValidate: true } })
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const HomePage = () => {
  return (
    <div className="w-full">
      <ResponsiveAlignedScrollableLayout className="py-4">
        <div className="max-w-[1400px] mx-auto px-4 space-y-6">
          <WelcomeHeader />
          <StatsSection />
          <UncategorizedSection />
        </div>
      </ResponsiveAlignedScrollableLayout>
    </div>
  )
}